import { Card, Title, Text } from '@nimbus-ds/components'
import { formatCurrency } from '@/lib/utils'

// Generic window-aggregate breakdown for ANY dimension (device, origin, …): sessions / share /
// conversions / CR / vs-average per bucket. Driven entirely by `rows`, so a new dimension is a
// different `rows` array, not a new component. See docs/specs/DIMENSIONS-ABSTRACTION-SPEC.md §3d.
// `revenue` is optional (Phase 2, REVENUE-ANALYTICS-SPEC): when ANY row carries it, the table adds
// Revenue + AOV columns; callers without revenue render unchanged.
export interface BreakdownRow {
  key: string
  label: string
  sessions: number
  conversions: number
  revenue?: number
}

interface BreakdownTableProps {
  title: string
  rows: BreakdownRow[]
  /** Optional honesty note (e.g. coverage / measurement-gap caveat). */
  note?: string
}

const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0)

export function BreakdownTable({ title, rows, note }: BreakdownTableProps) {
  const totalSessions = rows.reduce((a, r) => a + r.sessions, 0)
  const totalConv = rows.reduce((a, r) => a + r.conversions, 0)
  const totalRevenue = rows.reduce((a, r) => a + (r.revenue || 0), 0)
  // Show revenue columns only when some cell actually carries revenue (pre-Phase-2 / non-revenue
  // callers stay at the original 5 columns).
  const hasRevenue = rows.some((r) => typeof r.revenue === 'number') && totalRevenue > 0
  const avgCR = pct(totalConv, totalSessions)
  const sorted = rows.filter((r) => r.sessions > 0).sort((a, b) => b.sessions - a.sessions)

  if (totalSessions === 0) {
    return (
      <Card>
        <Card.Header><Title as="h4">{title}</Title></Card.Header>
        <Card.Body><Text>Sin datos en la ventana seleccionada.</Text></Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      <Card.Header><Title as="h4">{title}</Title></Card.Header>
      <Card.Body>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="pb-1"><Text fontSize="caption" as="span">Segmento</Text></th>
              <th className="pb-1 text-right"><Text fontSize="caption" as="span">Sesiones</Text></th>
              <th className="pb-1 text-right"><Text fontSize="caption" as="span">Conv.</Text></th>
              <th className="pb-1 text-right"><Text fontSize="caption" as="span">% Conv.</Text></th>
              {hasRevenue && <th className="pb-1 text-right"><Text fontSize="caption" as="span">Ingresos</Text></th>}
              {hasRevenue && <th className="pb-1 text-right"><Text fontSize="caption" as="span">Ticket prom.</Text></th>}
              <th className="pb-1 text-right"><Text fontSize="caption" as="span">vs prom.</Text></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => {
              const cr = pct(r.conversions, r.sessions)
              const vs = avgCR > 0 ? cr / avgCR : 1
              const share = pct(r.sessions, totalSessions)
              const aov = r.conversions > 0 && r.revenue ? r.revenue / r.conversions : null
              return (
                <tr key={r.key} className="border-t">
                  <td className="py-1"><Text as="span" color="neutral-textHigh">{r.label}</Text></td>
                  <td className="py-1 text-right tabular-nums"><Text as="span" color="neutral-textHigh">{r.sessions.toLocaleString()}</Text> <Text as="span" fontSize="caption">({share.toFixed(0)}%)</Text></td>
                  <td className="py-1 text-right tabular-nums"><Text as="span" color="neutral-textHigh">{r.conversions.toLocaleString()}</Text></td>
                  <td className="py-1 text-right tabular-nums"><Text as="span" color="neutral-textHigh">{cr.toFixed(1)}%</Text></td>
                  {hasRevenue && <td className="py-1 text-right tabular-nums"><Text as="span" color="neutral-textHigh">{r.revenue ? formatCurrency(r.revenue) : '—'}</Text></td>}
                  {hasRevenue && <td className="py-1 text-right tabular-nums"><Text as="span" color="neutral-textHigh">{aov !== null ? formatCurrency(aov) : '—'}</Text></td>}
                  <td className={`py-1 text-right tabular-nums ${vs >= 1 ? 'text-green-600' : 'text-red-600'}`}>{(vs * 100).toFixed(0)}%</td>
                </tr>
              )
            })}
            <tr className="border-t">
              <td className="py-1"><Text as="span" fontWeight="medium" color="neutral-textHigh">Total</Text></td>
              <td className="py-1 text-right tabular-nums"><Text as="span" fontWeight="medium" color="neutral-textHigh">{totalSessions.toLocaleString()}</Text></td>
              <td className="py-1 text-right tabular-nums"><Text as="span" fontWeight="medium" color="neutral-textHigh">{totalConv.toLocaleString()}</Text></td>
              <td className="py-1 text-right tabular-nums"><Text as="span" fontWeight="medium" color="neutral-textHigh">{avgCR.toFixed(1)}%</Text></td>
              {hasRevenue && <td className="py-1 text-right tabular-nums"><Text as="span" fontWeight="medium" color="neutral-textHigh">{formatCurrency(totalRevenue)}</Text></td>}
              {hasRevenue && <td className="py-1 text-right tabular-nums"><Text as="span" fontWeight="medium" color="neutral-textHigh">{totalConv > 0 ? formatCurrency(totalRevenue / totalConv) : '—'}</Text></td>}
              <td className="py-1 text-right" />
            </tr>
          </tbody>
        </table>
        {note && <Text fontSize="caption">{note}</Text>}
      </Card.Body>
    </Card>
  )
}
